import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Importing translation files

import translationEN from "./locales/en/translation.json";
import translationFR from "./locales/fr/translation.json";
import routesEN from "./locales/en/routes.json";
import routesFR from "./locales/fr/routes.json";
import seoEN from "./locales/en/seo.json";
import seoFR from "./locales/fr/seo.json";

const routeDictionaryEN = routesEN.routes || {};
const routeDictionaryFR = routesFR.routes || {};
const seoDictionaryEN = seoEN.seo || {};
const seoDictionaryFR = seoFR.seo || {};

//Creating object with the variables of imported translation files
const resources = {
  en: {
    translation: translationEN,
    routes: routeDictionaryEN,
    seo: seoDictionaryEN,
  },
  fr: {
    translation: translationFR,
    routes: routeDictionaryFR,
    seo: seoDictionaryFR,
  },
};

const TRANSLATABLE_ROUTE_KEYS = Object.keys(routeDictionaryEN).filter(
  (key) =>
    key !== "notFound" &&
    typeof routeDictionaryEN[key] === "string" &&
    typeof routeDictionaryFR[key] === "string"
);

export const normalizePathname = (pathname) => {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
};

const getVisiblePathsForDictionary = (routeDictionary) =>
  Object.entries(routeDictionary)
    .filter(([key, value]) => key !== "notFound" && typeof value === "string")
    .map(([, value]) => normalizePathname(value));

export const getNavbarVisiblePaths = () => [
  ...new Set([
    ...getVisiblePathsForDictionary(routeDictionaryEN),
    ...getVisiblePathsForDictionary(routeDictionaryFR),
  ]),
];

export const translatePathForLanguage = (pathname, targetLanguage) => {
  const normalizedPathname = normalizePathname(pathname);
  const targetRoutes = targetLanguage === "fr" ? routeDictionaryFR : routeDictionaryEN;

  const matchedRouteKey = TRANSLATABLE_ROUTE_KEYS.find((key) => {
    const enPath = normalizePathname(routeDictionaryEN[key]);
    const frPath = normalizePathname(routeDictionaryFR[key]);
    return normalizedPathname === enPath || normalizedPathname === frPath;
  });

  if (!matchedRouteKey) {
    return normalizedPathname;
  }

  return targetRoutes[matchedRouteKey];
};

const defaultLanguage = "en"; // Default language if no previous selection is found

// Get the previously selected language from browser storage
const selectedLanguage = localStorage.getItem("language");

// Set the initial language from storage or use the language from URL
let languageFromURL = selectedLanguage || defaultLanguage;
const normalizedCurrentPath = normalizePathname(window.location.pathname);
const isDistinctFrenchPath = Object.keys(routeDictionaryFR).some((key) => {
  if (key === "notFound") {
    return false;
  }

  const frPath = routeDictionaryFR[key];
  const enPath = routeDictionaryEN[key];

  return (
    typeof frPath === "string" &&
    typeof enPath === "string" &&
    normalizePathname(frPath) === normalizedCurrentPath &&
    normalizePathname(frPath) !== normalizePathname(enPath)
  );
});

if (isDistinctFrenchPath) {
  languageFromURL = "fr";
  localStorage.setItem("language", "fr");
} else {
  localStorage.setItem("language", languageFromURL);
}

//i18N Initialization

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    resources,
    fallbackLng: "en", // Default language if detection fails
    lng: languageFromURL, // Set initial language from URL
    // keySeparator: false,
    // interpolation: {
    //   escapeValue: false,
    // },
  });

// Set the language in browser storage when it changes
export const changeLanguage = async (lng) => {
  await i18n.changeLanguage(lng);
  localStorage.setItem("language", lng);
  languageFromURL = lng;
};

export const changeLanguageWithRouteTranslation = async ({
  lng,
  pathname,
  search = "",
  hash = "",
  navigate,
}) => {
  const translatedPathname = translatePathForLanguage(pathname, lng);

  await changeLanguage(lng);

  if (typeof navigate === "function") {
    navigate(`${translatedPathname}${search}${hash}`, { replace: true });
  }

  return translatedPathname;
};

export default i18n;
